// Канал почты: тела и запросы маршрутов delivery.* (008: contracts/api.md, «Маршруты», «Схемы»).
// Формат ключа Resend сервер проверяет сам и отвечает MAIL-003, а не REC-007 (FR-009).
import { z } from '../kernel/zod.ts';
import { DateSchema } from '../kernel/common.ts';
import { EMAIL_MAX, isEmailAddress } from '../kernel/email.ts';
import { fieldMessages } from '../kernel/field-messages.ts';
import { PageQuerySchema, pageSchema, sortSchema } from '../kernel/page.ts';
import { MailPlatformSchema } from '../delivery/connection.ts';
import {
  MailMessageKindSchema,
  MailMessageRowSchema,
  MailMessageStateSchema,
} from '../delivery/message.ts';

/** Ключ Resend: re_ и 8–200 знаков из латиницы, цифр, _ и -. */
export const MAIL_KEY_PATTERN = /^re_[A-Za-z0-9_-]{8,200}$/;

/** Пробных писем на установку за час (FR-020). */
export const TEST_LETTERS_PER_HOUR = 10;

const address = (message: string) =>
  z
    .string({ error: message })
    .trim()
    .max(EMAIL_MAX, { error: message })
    .refine(isEmailAddress, { error: message });

export const SaveMailConnectionBodySchema = z.object({
  platform: MailPlatformSchema,
  senderName: z
    .string({ error: fieldMessages.senderName })
    .trim()
    .min(1, { error: fieldMessages.senderName })
    .max(80, { error: fieldMessages.senderName })
    .regex(/^[^<>"\r\n]+$/, { error: fieldMessages.senderName }),
  senderAddress: address(fieldMessages.senderAddress),
  replyTo: address(fieldMessages.replyTo).nullable(),
  signature: z
    .string({ error: fieldMessages.mailSignature })
    .trim()
    .max(200, { error: fieldMessages.mailSignature }),
  contact: z
    .string({ error: fieldMessages.mailContact })
    .trim()
    .max(200, { error: fieldMessages.mailContact }),
  /**
   * 042 (FR-030): дата письменного согласия заказчика на полные письма через канал вне России;
   * null — отметку сняли, нет поля — не меняется. Изменение — журнал delivery.consent_changed.
   */
  fullConsentOn: DateSchema.nullable().optional(),
});
export type SaveMailConnectionBody = z.infer<typeof SaveMailConnectionBodySchema>;

/** Первое сохранение — If-Match: 0 (подключения ещё нет). */
export const MailConnectionVersionHeadersSchema = z.object({
  'if-match': z.coerce.number().int().min(0),
});

export const SetMailKeyBodySchema = z.object({
  key: z.string().trim().min(1).max(500),
});

export const SetMailSendingBodySchema = z.object({ enabled: z.boolean() });

export const mailMessageSortFields = ['createdAt'] as const;

/** GET /api/delivery/messages: q — номер или часть адреса, фильтры, новые сверху, курсор. */
export const MailMessagesQuerySchema = z.object({
  ...PageQuerySchema.shape,
  q: z.string().trim().max(200).optional(),
  state: MailMessageStateSchema.optional(),
  kind: MailMessageKindSchema.optional(),
  sort: sortSchema(mailMessageSortFields).default('-createdAt'),
});
export type MailMessagesQuery = z.input<typeof MailMessagesQuerySchema>;

export const MailMessagesPageSchema = pageSchema(MailMessageRowSchema);
